import { Notifier } from '@airbrake/browser'

import type { AirbrakeProject } from './config'
import {
  airbrakeEnvironment,
  BOT_UA_REGEX,
  isAirbrakeLive,
  keysBlocklist,
} from './config'

export { BOT_UA_REGEX }

type NotifyArgs = Parameters<Notifier['notify']>[0]

// Minimal structural view of a notice — only what the filters touch. All
// fields optional so the SDK's own notice type is assignable to it, and unit
// tests can build small fixtures.
export interface NoticeLike {
  context?: Record<string, unknown>
  params?: Record<string, unknown>
  session?: Record<string, unknown>
  errors?: Array<{
    type?: string
    message?: string
    backtrace?: Array<{ file?: string }>
  }>
}

export interface ClientFilterDeps {
  userAgent: string
  origin: string
  random: () => number
  userId: string | null
  version: string | undefined
}

// ---------------------------------------------------------------------------
// Pure, unit-testable filter pieces. isAirbrakeLive is false under jest, so
// filters can never be tested through the Notifier — they are exported and
// tested directly instead.
// ---------------------------------------------------------------------------

// Browser-extension code injected into our pages; its crashes are not ours.
export const EXTENSION_URL_REGEX = /^(chrome|moz|safari|safari-web)-extension:/

// React hydration mismatches: real bugs but epidemically noisy (extensions
// mutating the DOM cause a large false-positive share). Minified prod builds
// surface them as numbered errors — https://react.dev/errors/418.
export const HYDRATION_MESSAGE_REGEX =
  /Minified React error #(418|423|425)|Hydration failed|Text content does not match|error while hydrating/i

// Keep a sample at warning severity: visible as a *rate*, never paging anyone.
export const HYDRATION_SAMPLE_RATE = 0.1

// Breadcrumb URLs can carry query strings (?code=…, ?token=…). context is
// never key-filtered by the SDK, so strip at the source.
export function scrubBreadcrumbUrl(url: string): string {
  return url.split(/[?#]/)[0]
}

function scrubBreadcrumb(crumb: unknown): unknown {
  if (!crumb || typeof crumb !== 'object') return crumb
  const out: Record<string, unknown> = { ...(crumb as Record<string, unknown>) }
  for (const key of ['url', 'from', 'to']) {
    if (typeof out[key] === 'string')
      out[key] = scrubBreadcrumbUrl(out[key] as string)
  }
  return out
}

// The single client-side filter chain. Drops return null (never sent, never
// billed); everything that survives gets stamped with version, grouping root,
// and user identity. Order: cheap drops first, mutations last.
export function filterClientNotice(
  notice: NoticeLike,
  deps: ClientFilterDeps,
): NoticeLike | null {
  const firstError = notice.errors?.[0]

  // 1) Crawler traffic.
  if (BOT_UA_REGEX.test(deps.userAgent)) return null

  // 2) Errors originating in extension code. Only the TOP frame (the throw
  //    site) is checked: an extension frame deeper in the stack can appear
  //    under a genuine app bug (extensions wrap fetch/addEventListener).
  const topFile = firstError?.backtrace?.[0]?.file
  if (topFile && EXTENSION_URL_REGEX.test(topFile)) return null

  // 3) AbortError: a navigation cancelled an in-flight fetch. Expected.
  if (firstError?.type === 'AbortError') return null

  if (!notice.context) notice.context = {}
  const ctx = notice.context

  // 4) ApolloErrors arriving via the GLOBAL handlers only (the SDK marks
  //    those with context.windowError / context.unhandledRejection). The
  //    Apollo errorLink is the designated GraphQL reporter (PR 4) and its
  //    notice carries strictly richer context; this prevents the one real
  //    double-report path (un-.catch()ed mutation promise → unhandledrejection).
  if (
    firstError?.type === 'ApolloError' &&
    (ctx.windowError === true || ctx.unhandledRejection === true)
  ) {
    return null
  }

  // 5) Hydration mismatches: sampled to HYDRATION_SAMPLE_RATE at warning
  //    severity (sub-error severities never trigger notifications).
  if (firstError?.message && HYDRATION_MESSAGE_REGEX.test(firstError.message)) {
    if (deps.random() >= HYDRATION_SAMPLE_RATE) return null
    ctx.severity = 'warning'
  }

  // 6) Stamps: deploy correlation (matches the SHA in deploy tracking),
  //    cross-hostname grouping (preview deploys get unique hostnames;
  //    rootDirectory lets Airbrake group their frames together), identity
  //    (userId ONLY — deliberate GLBA-minimization decision).
  if (deps.version) ctx.version = deps.version
  ctx.rootDirectory = deps.origin
  if (deps.userId) ctx.user = { id: deps.userId }

  // 7) Belt-and-suspenders breadcrumb scrub (console crumbs are disabled at
  //    the source; fetch/xhr/navigation crumbs keep method/status but lose
  //    query strings).
  if (Array.isArray(ctx.history)) {
    ctx.history = ctx.history.map(scrubBreadcrumb)
  }

  return notice
}

// ---------------------------------------------------------------------------
// Singleton wiring
// ---------------------------------------------------------------------------

let project: AirbrakeProject | null = null
let userId: string | null = null
let instance: Notifier | null = null

// Stamped as context.user = { id } on every subsequent notice. Call from
// _app.tsx when session props carry a user; call with null on logout.
export function setAirbrakeUser(id: string | null) {
  userId = id
}

function getNotifier(): Notifier | null {
  if (typeof window === 'undefined') return null
  if (!isAirbrakeLive) return null
  if (!project) {
    // Fail closed: a visible warning beats silently reporting to the wrong
    // project (the old hardcoded-fallback behavior).
    console.warn(
      '[airbrake] client not configured — call initAirbrakeClient(project) from instrumentation-client.ts',
    )
    return null
  }
  if (!instance) {
    instance = new Notifier({
      projectId: project.projectId,
      projectKey: project.projectKey,
      environment: airbrakeEnvironment,
      keysBlocklist,
      remoteConfig: false,
      //  This stops the per-fetch /performance/queries and
      // per-navigation /performance/routes POSTs without disabling the
      // fetch/xhr/history wrappers that capture breadcrumbs — breadcrumbs
      // still attach to error notices for debugging context.
      performanceStats: false,
      queryStats: false,
      // Console breadcrumbs record FULL console.* arguments into
      // context.history, which the SDK never key-filters — an uncontrolled
      // PII channel (the Apollo errorLink logged Authorization headers
      // through it). Any future console.error(obj) would re-open it, so the
      // channel is off for good. fetch/xhr/history/DOM crumbs stay on:
      // method/url/status only.
      instrumentation: { console: false },
    })
    instance.addFilter(
      (notice) =>
        // deps are rebuilt per notice, so setAirbrakeUser() after boot is
        // picked up by later notices. The cast re-asserts the SDK's notice
        // type: NoticeLike is a deliberately looser structural supertype
        // (all-optional, for unit-test fixtures), and the filter returns the
        // same object it receives — the type never actually changes.
        filterClientNotice(notice, {
          userAgent: navigator.userAgent,
          origin: window.location.origin,
          random: Math.random,
          userId,
          version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
        }) as typeof notice | null,
    )
  }
  return instance
}

// Constructs the Notifier eagerly so @airbrake/browser installs its global
// window.onerror + unhandledrejection handlers at app boot. Wired up from each
// app's instrumentation-client.ts, which passes that app's project (per-app
// injection: this lib must stay app-agnostic). Without this, errors that
// don't bubble through React's render tree (event handlers, async, promise
// rejections) are silently dropped until the first componentDidCatch.
export function initAirbrakeClient(appProject: AirbrakeProject) {
  project = appProject
  getNotifier()
}

export function notifyClient(args: NotifyArgs) {
  if (!isAirbrakeLive) {
    console.warn('[airbrake:dev] would have reported in prod:', args)
    return
  }
  getNotifier()?.notify(args)
}

export { ErrorBoundary } from './ErrorBoundary'
